import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleHomeClick = () => {
    navigate('/');
    setIsOpen(false);
  };

  const handleSectionClick = (sectionId: string) => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    setIsOpen(false);
  };

  const handleAboutPageClick = () => {
    navigate('/about-mystic-forest');
    setIsOpen(false);
  };

  const handleAboutPageSection = (sectionId: string) => {
    navigate('/about-mystic-forest');
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-background/90 backdrop-blur-md border-b border-border/20 shadow-sm" aria-label="Main navigation">
      {/* Skip to main content - Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
      >
        Skip to main content
      </a>
      
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-20">
          <button onClick={handleHomeClick} className="flex items-center gap-3 hover:opacity-80 transition-all duration-300 group" aria-label="Go to homepage">
            <div className="text-2xl font-serif font-black">
              <span className="text-foreground">Mystic Forest</span>
            </div>
          </button>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-6">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-accent transition-all duration-300 font-medium relative group" aria-label="Our Roots menu">
                Our Roots
                <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" />
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-popover border border-border shadow-xl rounded-xl p-2 z-[60]">
                <DropdownMenuItem 
                  onClick={() => handleAboutPageSection('manifest')} 
                  className="cursor-pointer rounded-lg hover:bg-accent/10 transition-colors duration-200"
                >
                  Manifesto
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleAboutPageSection('values')} 
                  className="cursor-pointer rounded-lg hover:bg-accent/10 transition-colors duration-200"
                >
                  The Path We Follow
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleAboutPageSection('why-now')} 
                  className="cursor-pointer rounded-lg hover:bg-accent/10 transition-colors duration-200"
                >
                  Why Now?
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleAboutPageSection('technology')} 
                  className="cursor-pointer rounded-lg hover:bg-accent/10 transition-colors duration-200"
                >
                  Our Relationship with Technology
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleAboutPageSection('about')} 
                  className="cursor-pointer rounded-lg hover:bg-accent/10 transition-colors duration-200"
                >
                  My Journey into the Forest
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <button 
              onClick={() => handleSectionClick('services')} 
              className="text-foreground hover:text-accent transition-all duration-300 font-medium relative group"
              aria-label="Navigate to Paths We Offer section"
            >
              Paths We Offer
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
            </button>

            <button 
              onClick={() => handleSectionClick('experiences')} 
              className="text-foreground hover:text-accent transition-all duration-300 font-medium relative group"
              aria-label="Navigate to Upcoming Experiences section"
            >
              Upcoming Experiences
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-accent transition-all duration-300 font-medium relative group" aria-label="Echoes of the Forest blog menu">
                Echoes of the Forest
                <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" />
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-popover border border-border shadow-xl rounded-xl p-2 z-[60]">
                <DropdownMenuItem 
                  onClick={() => handleSectionClick('blog')} 
                  className="cursor-pointer rounded-lg hover:bg-accent/10 transition-colors duration-200"
                >
                  Latest Articles
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    navigate('/blog');
                    setIsOpen(false);
                  }} 
                  className="cursor-pointer rounded-lg hover:bg-accent/10 transition-colors duration-200"
                >
                  All Articles
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button 
              onClick={() => handleSectionClick('store')} 
              className="text-foreground hover:text-accent transition-all duration-300 font-medium relative group"
              aria-label="Navigate to The Slow Shop section"
            >
              The Slow Shop
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
            </button>

            <button 
              onClick={() => handleSectionClick('leader')} 
              className="text-foreground hover:text-accent transition-all duration-300 font-medium relative group"
              aria-label="Navigate to Join the Club section"
            >
              Join the Club
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-foreground hover:text-accent p-2 rounded-lg hover:bg-accent/10 transition-all duration-300"
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-6 space-y-4 border-t border-border/20 bg-background/95 backdrop-blur-md">
            <div className="space-y-2">
              <div className="px-4 py-2 text-sm text-muted-foreground font-medium">Our Roots</div>
              <button 
                onClick={() => handleAboutPageSection('manifest')} 
                className="block w-full text-left px-6 py-2 text-foreground hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-200"
              >
                Manifesto
              </button>
              <button 
                onClick={() => handleAboutPageSection('values')} 
                className="block w-full text-left px-6 py-2 text-foreground hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-200"
              >
                The Path We Follow
              </button>
              <button 
                onClick={() => handleAboutPageSection('why-now')} 
                className="block w-full text-left px-6 py-2 text-foreground hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-200"
              >
                Why Now?
              </button>
              <button 
                onClick={() => handleAboutPageSection('technology')} 
                className="block w-full text-left px-6 py-2 text-foreground hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-200"
              >
                Our Relationship with Technology
              </button>
              <button 
                onClick={() => handleAboutPageSection('about')} 
                className="block w-full text-left px-6 py-2 text-foreground hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-200"
              >
                My Journey into the Forest
              </button>
            </div>
            
            <button 
              onClick={() => handleSectionClick('services')} 
              className="block w-full text-left px-4 py-3 text-foreground hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-200"
            >
              Paths We Offer
            </button>

            <button 
              onClick={() => handleSectionClick('experiences')} 
              className="block w-full text-left px-4 py-3 text-foreground hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-200"
            >
              Upcoming Experiences
            </button>

            <div className="space-y-2">
              <div className="px-4 py-2 text-sm text-muted-foreground font-medium">Echoes of the Forest</div>
              <button 
                onClick={() => handleSectionClick('blog')} 
                className="block w-full text-left px-6 py-2 text-foreground hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-200"
              >
                Latest Articles
              </button>
              <button 
                onClick={() => {
                  navigate('/blog');
                  setIsOpen(false);
                }} 
                className="block w-full text-left px-6 py-2 text-foreground hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-200"
              >
                All Articles
              </button>
            </div>

            <button 
              onClick={() => handleSectionClick('store')} 
              className="block w-full text-left px-4 py-3 text-foreground hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-200"
            >
              The Slow Shop
            </button>

            <button 
              onClick={() => handleSectionClick('leader')} 
              className="block w-full text-left px-4 py-3 text-foreground hover:text-accent hover:bg-accent/10 rounded-lg transition-all duration-200"
            >
              Join the Club
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};